import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Phone, Mail, Calendar, Building2, MapPin, Plus } from "lucide-react";
import { useState } from "react";
import { useLoan } from "@/context/LoanContext";
import { useLoanContacts } from "@/hooks/useLoanQuery";

export const LoanOfficerDialog = ({ children }: { children: React.ReactNode }) => {
  const [showAddress, setShowAddress] = useState(false);
  const { loanId } = useLoan();
  const { data: contactsData } = useLoanContacts(loanId);
  const lo = contactsData?.contacts?.find(
    (c) => c.role === 'Loan Officer' || c.role === 'loan_officer'
  );

  const loName = lo?.name ?? 'Your Loan Officer';
  const loInitials = lo
    ? lo.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : 'LO';
  const loEmail = lo?.email ?? '';
  const loPhone = lo?.phone ?? '';
  const loFirstName = lo?.name?.split(' ')[0] ?? 'your loan officer';

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <div className="p-6 space-y-5">
          <DialogHeader className="space-y-1.5 text-left">
            <DialogTitle className="text-lg">Your Loan Officer</DialogTitle>
            <DialogDescription className="text-[13px]">
              Your dedicated point of contact for loan guidance.
            </DialogDescription>
          </DialogHeader>

          {/* Profile */}
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-full bg-info/15 text-info grid place-items-center text-base font-semibold shrink-0 shadow-sm">
              {loInitials}
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-base">{loName}</div>
              <div className="text-[13px] text-muted-foreground">Loan Officer</div>
              <span className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium bg-accent-soft text-gold">
                <Building2 className="size-3" /> American Financial Network
              </span>
            </div>
          </div>

          {/* Best for */}
          <div className="rounded-xl border border-border/60 bg-muted/40 p-3.5">
            <p className="text-[12.5px] italic text-muted-foreground leading-relaxed">
              Best for: loan strategy, rates, offer advice, and any general questions about your mortgage.
            </p>
          </div>

          {/* Primary CTA */}
          {loPhone && (
            <a
              href={`tel:${loPhone}`}
              className="w-full h-11 rounded-xl bg-primary text-primary-foreground font-medium text-sm inline-flex items-center justify-center gap-2 transition-all duration-200 hover:brightness-105 hover:shadow-[0_8px_20px_-8px_hsl(var(--foreground)/0.25)]"
            >
              <Phone className="size-4" /> Call {loFirstName}
            </a>
          )}

          {/* Secondary actions */}
          <div className="grid grid-cols-2 gap-2.5">
            {loEmail && (
              <a
                href={`mailto:${loEmail}`}
                className="h-11 rounded-xl border border-[hsl(218_55%_32%)] text-[hsl(218_55%_32%)] bg-background font-medium text-sm inline-flex items-center justify-center gap-2 dark:border-secondary-300 dark:text-secondary-300 dark:bg-transparent transition-all duration-200 hover:brightness-105 hover:shadow-[0_8px_20px_-8px_hsl(var(--foreground)/0.25)]"
              >
                <Mail className="size-4" /> Email
              </a>
            )}
            <button className="h-11 rounded-xl border border-[hsl(218_55%_32%)] text-[hsl(218_55%_32%)] bg-background font-medium text-sm inline-flex items-center justify-center gap-2 dark:border-secondary-300 dark:text-secondary-300 dark:bg-transparent transition-all duration-200 hover:brightness-105 hover:shadow-[0_8px_20px_-8px_hsl(var(--foreground)/0.25)]">
              <Calendar className="size-4" /> Schedule
            </button>
          </div>

          {/* Contact details */}
          <div className="pt-1 space-y-2 text-[13px] border-t border-border/60">
            {loPhone && (
              <div className="flex items-center gap-2 text-muted-foreground pt-3">
                <Phone className="size-3.5" /> {loPhone}
              </div>
            )}
            {loEmail && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Mail className="size-3.5" /> {loEmail}
              </div>
            )}
            <button
              onClick={() => setShowAddress((v) => !v)}
              className="flex items-center justify-between w-full text-[13px] pt-1 hover:text-foreground text-muted-foreground"
            >
              <span>{showAddress ? "Hide office address" : "Show office address"}</span>
              <Plus className={`size-3.5 transition-transform ${showAddress ? "rotate-45" : ""}`} />
            </button>
            {showAddress && (
              <div className="flex items-start gap-2 text-muted-foreground animate-fade-in-up">
                <MapPin className="size-3.5 mt-0.5" />
                <span>10 Pointe Drive, Suite 330<br />Brea, CA 92821</span>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
